import {
	getLinkedNodes,
	hasMovedNoticably,
	hexOrNamedColour,
	removeEl,
	removeNoDeleteAnimation,
} from './graphUtils';
import {
	getNodeOrigin,
	isBoardAnchorNode,
	nodeOriginRepeatsLabel,
	NodeOrigin,
} from './nodeOrigin';
import * as d3 from 'd3';
import { GraphProps, MenuOption } from '../../types';
import { PieArcDatum } from 'd3';
import { GraphMode, NCNode } from '../../../../src/commonTypes';
import { detectBrowser } from '../../../../src/commonMisc';
import { formatPdfDisplayName } from '../../../../src/pdfFileName';
import { toReadablePositioningOutput } from '../../../../src/positioningOutputs';
import { getNodeLikeActionLabel, getNodeLikers, isNodeLikedByUser } from './nodeLikes';
import { HEART_COLOURS, HEART_POSITIONS } from '../constants';
import { getSynthesiseOutputTitle } from '../ui/synthesiseOutputTitle';

// Track the most recent input update time to filter out 'change' events
let lastInputUpdateTs = 0;

// Rect node sizing. The rect background grows with the label up to MAX_RECT_HEIGHT,
// then clamps and the inner textarea scrolls. RECT_TOP_PAD / RECT_BOTTOM_PAD reserve
// space for the origin badge / link button (top) and the hearts strip (bottom).
// RECT_SIDE_PAD is the horizontal breathing room between the textarea and the rect
// border.
const RECT_WIDTH = 4;
const RECT_ANCHOR_WIDTH = 5.5;
const MAX_RECT_HEIGHT = 480;
const RECT_TOP_PAD = 38;
const RECT_BOTTOM_PAD = 30;
const RECT_SIDE_PAD = 14;
const RECT_INPUT_MIN_HEIGHT = 28;
const RECT_MIN_OUTER_HEIGHT = 92;
const RECT_BADGE_HEIGHT = 30;
const RECT_BADGE_INSET_X = 8;
const RECT_BADGE_INSET_Y = 5;
const RECT_LINK_BUTTON_SIZE = 18;
const RECT_LIKE_CONTROL_WIDTH = 48;
const RECT_META_LIKE_GAP = 8;
const CARD_ACTION_RIGHT_INSET = 8;
const CARD_ACTION_GAP = 6;
const RECT_EXPAND_CONTROL_WIDTH = 28;
// Conservative single-line textarea height used before the browser measures
// the real content. Picked so a freshly appended empty rect lands at the
// RECT_MIN_OUTER_HEIGHT floor without a layout jump.
const RECT_FALLBACK_NATURAL_HEIGHT = 31;
const IMAGE_CARD_WIDTH = 184;
const MEDIA_CARD_PREVIEW_HEIGHT = 150;
const IMAGE_CARD_HEIGHT = 220;
// Match project-vision / board-anchor visual weight: taller card so the
	// summary reads as a primary board context node rather than a compact chip.
	const PDF_DOCUMENT_CARD_HEIGHT = 176;
const SYNTHESISE_OUTPUT_MAX_HEIGHT = 280;
const SYNTHESISE_HEADER_HEIGHT = 28;
const POSITIONING_CARD_HEIGHT = 252;
const POSITIONING_PERSONA_CARD_HEIGHT = 320;
const POSITIONING_CARD_CONTENT_INSET = 16;
const GROUNDING_EVIDENCE_CARD_HEIGHT = 270;
const GROUNDING_EVIDENCE_TEXT_CARD_HEIGHT = 164;

/** Image and video nodes share the same rounded media-card chrome and layout. */
const isMediaCardNode = (node: NCNode): boolean => Boolean(node.src);
const isPdfDocumentNode = (node: NCNode): boolean =>
	Boolean(node.pdfAnalysisId && !node.pdfInsightId);
const isTextSynthesiseOutputNode = (node: NCNode): boolean =>
	Boolean(node.synthesiseRecipe && node.label && !node.src && node.style === 'rect');
const isPositioningOutputNode = (node: NCNode): boolean =>
	Boolean(node.positioningOutput && node.style === 'rect' && !node.src);
// Grounding metadata is attached to every evidence node so it can be traced
// back to its source. Only actual social posts use the rich post-card shell;
// FOCUS, PDF, Idea Box, and board-concept evidence retain their normal Inspire
// node treatment.
const isGroundingEvidenceCard = (node: NCNode): boolean =>
	Boolean(node.groundingEvidence?.source.startsWith('social-profile:') && node.style === 'rect');
const isWebResultCardNode = (node: NCNode): boolean => {
	const origin = getNodeOrigin(node)?.key;
	return Boolean(node.href && ['web', 'pubmed', 'gdelt', 'link'].includes(origin || ''));
};
const getGroundingEvidenceCardHeight = (node: NCNode): number =>
	node.groundingEvidence?.image
		? GROUNDING_EVIDENCE_CARD_HEIGHT
		: GROUNDING_EVIDENCE_TEXT_CARD_HEIGHT;
const getPositioningCardHeight = (node: NCNode): number =>
	['persona', 'character'].includes(node.positioningOutput?.type || '')
		? POSITIONING_PERSONA_CARD_HEIGHT
		: POSITIONING_CARD_HEIGHT;

const getImageCardHeartPosition = (index: number): { x: number; y: number } => {
	const heartsPerRow = 9;
	return {
		x: -IMAGE_CARD_WIDTH / 2 + 8 + (index % heartsPerRow) * 18,
		y: IMAGE_CARD_HEIGHT / 2 - 20 - Math.floor(index / heartsPerRow) * 18,
	};
};

/**
 * Empathise colours normally become the node fill and therefore need white
 * text. PDF-backed results use the light PDF card surface instead, even when
 * they retain an Empathise colour for provenance, so their text must stay dark.
 */
export const shouldUseInversedNodeText = (node: NCNode, graphMode: GraphMode): boolean =>
	Boolean(node.nodeColour && graphMode !== 'ideate' && !node.pdfAnalysisId);

export const getPdfDisplayName = (node: NCNode): string => {
	return formatPdfDisplayName(node.pdfFileName);
};

type MeasurableNode = { __measuredRectHeight?: number };

const readMeasuredRectHeight = (node: NCNode | null | undefined): number | null => {
	const cached = (node as (NCNode & MeasurableNode) | null | undefined)?.__measuredRectHeight;
	if (typeof cached === 'number' && Number.isFinite(cached) && cached > 0) {
		return cached;
	}
	return null;
};

const writeMeasuredRectHeight = (node: NCNode, height: number): void => {
	(node as NCNode & MeasurableNode).__measuredRectHeight = height;
};

const getRectWidthMultiplier = (node?: NCNode | null): number =>
	node &&
	(isBoardAnchorNode(node) || isPositioningOutputNode(node) || isPdfDocumentNode(node))
		? RECT_ANCHOR_WIDTH
		: RECT_WIDTH;

const getRectInputContentWidth = (nodeRadius: number, node?: NCNode | null): number =>
	getRectWidthMultiplier(node) * nodeRadius - 2 * RECT_SIDE_PAD;

const clampOuterHeight = (natural: number, maxHeight = MAX_RECT_HEIGHT): number =>
	Math.min(maxHeight, Math.max(RECT_MIN_OUTER_HEIGHT, natural + RECT_TOP_PAD + RECT_BOTTOM_PAD));

const clampInnerHeight = (natural: number, outer: number): number => {
	const available = outer - RECT_TOP_PAD - RECT_BOTTOM_PAD;
	return Math.max(RECT_INPUT_MIN_HEIGHT, Math.min(natural, available));
};

const computeRectInputY = (outer: number, inner: number): number => {
	const innerTop = -outer / 2 + RECT_TOP_PAD;
	const innerBottom = outer / 2 - RECT_BOTTOM_PAD;
	const innerHeight = Math.max(0, innerBottom - innerTop);
	return innerTop + Math.max(0, (innerHeight - inner) / 2);
};

const getNodeLikeControlPosition = (node: NCNode, nodeRadius: number): { x: number; y: number } => {
	if (isGroundingEvidenceCard(node)) {
		return {
			x:
				(getRectWidthMultiplier(node) * nodeRadius) / 2 -
				RECT_LIKE_CONTROL_WIDTH -
				CARD_ACTION_RIGHT_INSET,
			y: -getGroundingEvidenceCardHeight(node) / 2 + 6,
		};
	}
	if (isMediaCardNode(node)) {
		return {
			x: IMAGE_CARD_WIDTH / 2 - RECT_LIKE_CONTROL_WIDTH - CARD_ACTION_RIGHT_INSET,
			y: -IMAGE_CARD_HEIGHT / 2 + 8,
		};
	}
	if (node.style === 'rect') {
		if (isPositioningOutputNode(node)) {
			return {
				x:
					(getRectWidthMultiplier(node) * nodeRadius) / 2 -
					RECT_LIKE_CONTROL_WIDTH -
					POSITIONING_CARD_CONTENT_INSET,
				y: -getRectNodeHeight(node, nodeRadius) / 2 + POSITIONING_CARD_CONTENT_INSET,
			};
		}
		return {
			x:
				(getRectWidthMultiplier(node) * nodeRadius) / 2 -
				RECT_LIKE_CONTROL_WIDTH -
				CARD_ACTION_RIGHT_INSET,
			y: -getRectNodeHeight(node, nodeRadius) / 2 + 8,
		};
	}
	return {
		x: nodeRadius - RECT_LIKE_CONTROL_WIDTH - CARD_ACTION_RIGHT_INSET,
		y: -nodeRadius - 4,
	};
};

const updateNodeLikeControls = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	const controls = nodes.select<SVGGElement>('g.node-like-control');
	controls
		.attr('transform', (node) => {
			const { x, y } = getNodeLikeControlPosition(node, graphProps.nodeRadius);
			return `translate(${x}, ${y})`;
		})
		.classed('is-liked', (node) => isNodeLikedByUser(node, graphProps.currentUser));
	controls
		.select<SVGImageElement>('.node-like-heart-icon')
		.attr('href', (node) =>
			isNodeLikedByUser(node, graphProps.currentUser)
				? '/assets/icon/board/heart-full.png'
				: '/assets/icon/board/heart-empty.png'
		);
	controls
		.select<SVGGElement>('.node-like-heart-button')
		.attr('aria-pressed', (node) => String(isNodeLikedByUser(node, graphProps.currentUser)))
		.attr('aria-label', (node) =>
			isNodeLikedByUser(node, graphProps.currentUser) ? 'Unlike node' : 'Like node'
		);
	controls
		.select<SVGTextElement>('.node-like-count')
		.text((node) => String(getNodeLikers(node, graphProps.boardUsers).length));
	controls.select<SVGGElement>('.node-like-count-group').attr('aria-label', (node) => {
		const count = getNodeLikers(node, graphProps.boardUsers).length;
		return `Show who liked this node; ${count} total likes`;
	});
	controls.each(function (node) {
		const likers = getNodeLikers(node, graphProps.boardUsers);
		const content = this.querySelector<HTMLElement>('.node-like-popover-content');
		if (!content) return;
		content.replaceChildren();
		const title = document.createElement('strong');
		title.textContent =
			likers.length === 1 ? 'Liked by 1 person' : `Liked by ${likers.length} people`;
		content.append(title);
		if (likers.length === 0) {
			const empty = document.createElement('span');
			empty.textContent = 'No likes yet';
			content.append(empty);
		} else {
			likers.forEach((user) => {
				const row = document.createElement('span');
				const dot = document.createElement('i');
				dot.style.backgroundColor = hexOrNamedColour(user.colour);
				row.append(dot, document.createTextNode(user.username));
				content.append(row);
			});
		}
		this.querySelector('title')!.textContent = likers.length
			? `Liked by ${likers.map((user) => user.username).join(', ')}`
			: 'No likes yet';
	});
};

/**
 * Measures the natural content height of a rect-node textarea by clearing its
 * inline height constraints and reading `scrollHeight`. The caller is expected
 * to write the resulting `inner` height back to the textarea immediately. The
 * textarea must already be in the DOM with a fixed `width` (set via the parent
 * foreignObject) and the final `font-size` applied before measuring.
 *
 * `rows=1` is forced for the measurement so the textarea's intrinsic minimum
 * doesn't artificially inflate `scrollHeight` (a default `rows=2` or a stale
 * `rows=6` from a previous update would otherwise add 1+ blank lines of
 * whitespace beneath the content). The textarea's CSS uses `box-sizing:
 * border-box`, so the returned natural height already accounts for the
 * textarea's own padding and border.
 */
const measureRectHeightsForTextarea = (
	textarea: HTMLTextAreaElement,
	maxOuterHeight = MAX_RECT_HEIGHT
): { outer: number; inner: number } => {
	textarea.rows = 1;
	textarea.style.minHeight = 'auto';
	textarea.style.maxHeight = 'none';
	textarea.style.height = 'auto';
	const raw = textarea.scrollHeight + 10;
	const natural = raw > 0 ? raw : RECT_FALLBACK_NATURAL_HEIGHT;
	const outer = clampOuterHeight(natural, maxOuterHeight);
	const inner = clampInnerHeight(natural, outer);
	return { outer, inner };
};

/**
 * Returns the SVG height for a rectangular phrase node. Browser measurement
 * lives in `updateLabelInput`; this function returns the latest measurement
 * cached on the node, or a stable `RECT_MIN_OUTER_HEIGHT` floor before the
 * first measurement. The floor keeps collision physics and first-paint badge
 * positioning sensible even when the textarea hasn't been laid out yet.
 */
export const getRectNodeHeight = (node: NCNode | null | undefined, nodeRadius: number): number => {
	void nodeRadius;
	return node && isGroundingEvidenceCard(node)
		? getGroundingEvidenceCardHeight(node)
		: node && isPositioningOutputNode(node)
		? getPositioningCardHeight(node)
		: (readMeasuredRectHeight(node) ?? RECT_MIN_OUTER_HEIGHT);
};

export const getRectNodeWidth = (node: NCNode | null | undefined, nodeRadius: number): number =>
	getRectWidthMultiplier(node) * nodeRadius;

export const getNodeCollisionSize = (
	node: NCNode,
	nodeRadius: number
): { width: number; height: number } => {
	if (isMediaCardNode(node)) {
		return { width: IMAGE_CARD_WIDTH, height: IMAGE_CARD_HEIGHT };
	}
	if (node.style === 'rect') {
		return {
			width: getRectNodeWidth(node, nodeRadius),
			height: getRectNodeHeight(node, nodeRadius),
		};
	}
	const diameter = nodeRadius * 2;
	return { width: diameter, height: diameter };
};

// Number of lines for the input field in the node
const getNumberLines = (label: string, nodeRadius: number, isRect: boolean): number => {
	if (isRect) {
		if (label.length > 260) {
			return 9;
		} else if (label.length > 220) {
			return 8;
		} else if (label.length > 180) {
			return 7;
		} else if (label.length > 150) {
			return 6;
		} else if (label.length > 120) {
			return 5;
		} else if (label.length > 80) {
			return 4;
		} else if (label.length > 40) {
			return 3;
		} else {
			return 2;
		}
	}

	if (nodeRadius > 50) {
		if (label.length > 50) {
			return 4;
		}
		if (label.length > 32) {
			return 3;
		}
		if (label.length > 12) {
			return 2;
		}
		return 1;
	} else {
		if (label.length > 32) {
			return 4;
		}
		if (label.length > 20) {
			return 3;
		}
		if (label.length > 10) {
			return 2;
		}
		return 1;
	}
};

const getInputHeight = (label: string, nodeRadius: number, isRect: boolean): number => {
	const numLines = getNumberLines(label, nodeRadius, isRect);
	switch (numLines) {
		case 9:
		case 8:
			return 130;
		case 7:
			return 113;
		case 6:
			return 100;
		case 5:
			return 90;
		case 4:
			return isRect ? 74 : 64;
		case 3:
			return isRect ? 62 : 53;
		case 2:
			return isRect ? 49 : 39;
		case 1:
		default:
			return 26;
	}
};

const getFontSize = (d: NCNode, nodeRadius: number): string => {
	if (d.style === 'rect') {
		if (d.synthesiseRecipe) return '13px';
		const anchorBoost = isBoardAnchorNode(d) ? 1 : 0;
		if (d.label) {
			if (d.label.length > 600) {
				return `${7 + anchorBoost}px`;
			} else if (d.label.length > 400) {
				return `${8 + anchorBoost}px`;
			} else if (d.label.length > 300) {
				return `${9 + anchorBoost}px`;
			} else if (d.label.length > 250) {
				return `${10 + anchorBoost}px`;
			} else if (d.label.length > 220) {
				return `${11 + anchorBoost}px`;
			} else if (d.label.length > 180) {
				return `${12 + anchorBoost}px`;
			} else if (d.label.length > 150) {
				return `${13 + anchorBoost}px`;
			} else if (d.label.length > 80) {
				return `${14 + anchorBoost}px`;
			} else if (d.label.length > 40) {
				return `${15 + anchorBoost}px`;
			}
		}
		return `${16 + anchorBoost}px`;
	}
	return `${
		15 - getNumberLines(d.label || '', nodeRadius, d.style === 'rect') - (nodeRadius > 50 ? 0 : 1)
	}px`;
};

export const generateNodeId = () => `O-${Math.random().toString(16).slice(2)}`;

const nodeKeyFn = (d: NCNode) => d.uid;

// The group in which nodes (circles) will be placed
export const createNodes = (
	nodesElement: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodes: Array<NCNode>,
	enterNode: (
		elem: d3.Selection<d3.EnterElement, NCNode, SVGGElement, null>
	) => d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	updateNode: (
		elem: d3.Selection<SVGGElement, NCNode, SVGGElement, null>
	) => d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	isDeleteAnimated: boolean
): d3.Selection<SVGGElement, NCNode, SVGGElement, null> =>
	nodesElement
		.data<NCNode>(nodes, nodeKeyFn)
		.join<SVGGElement>(
			enterNode,
			updateNode,
			isDeleteAnimated ? removeEl : removeNoDeleteAnimation
		);

const parseTransform = (transform: string | null): { x: number; y: number } => {
	if (!transform) {
		return { x: 0, y: 0 };
	}
	const translate = transform.match(/translate\(([^,]+),([^,]+)\)/);
	if (!translate) {
		return { x: 0, y: 0 };
	}
	return { x: parseFloat(translate[1]), y: parseFloat(translate[2]) };
};

// Only updates the position of the nodes
export const updateNodePositions = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>
): void => {
	node.each(function (d) {
		const oldTranslate = this.getAttribute('transform');
		const oldPos = parseTransform(oldTranslate);
		if (hasMovedNoticably(oldPos, d)) {
			this.setAttribute('transform', `translate(${d.x}, ${d.y})`);
		}
	});
	// Disabled as this is inefficient
	// node.attr('transform', (d) => `translate(${d.x}, ${d.y})`);
};

export const updateNodes = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	// Updates the position of the nodes (circle/rect) based on node data.
	updateNodePositions(node);

	// Applies 'hidden' class to nodes that are flagged as hidden.
	node.classed('hidden', (d) => Boolean(d.hidden));
	node.classed('board-anchor', (d) => isBoardAnchorNode(d));
	node
		.classed('node--pdf', (d) => Boolean(d.pdfAnalysisId || getNodeOrigin(d)?.key === 'pdf'))
		.classed('node--pdf-document', isPdfDocumentNode)
		.classed('node--synthesise-output', (d) => Boolean(d.synthesiseRecipe))
		.classed('node--positioning-output', isPositioningOutputNode)
		.classed('node--vision', (d) => isBoardAnchorNode(d))
		.classed('node--image', (d) => Boolean(d.src && !d.isVideo))
		.classed('node--video', (d) => Boolean(d.src && d.isVideo))
		.classed('node--search', isWebResultCardNode)
		.classed(
			'node--concept',
			(d) => d.style === 'rect' && !d.pdfAnalysisId && !isBoardAnchorNode(d) && !d.src
		)
		.classed('node--quick-word', (d) => d.style !== 'rect' && !d.src);

	// Sets the stroke color for circle nodes based on whether node coloring is enabled.
	node
		.select('circle.node')
		.attr('stroke', graphProps.areNodesColoured ? (d) => hexOrNamedColour(d.colour) : '#91909A');

	// Sets the stroke color for rectangle nodes based on whether node coloring is enabled.
	node
		.select('rect.node')
		.attr('stroke', (d) => (graphProps.areNodesColoured ? hexOrNamedColour(d.colour) : '#91909A'));

	// Updates node shape from circle to rectangle for nodes that should be displayed as rectangles.
	const rectNode = node.filter((d) => d.style === 'rect');
	rectNode
		.select('rect.node')
		.attr('width', (d) => getRectWidthMultiplier(d) * graphProps.nodeRadius)
		.attr('height', (d) => getRectNodeHeight(d, graphProps.nodeRadius))
		.attr('x', (d) => -(getRectWidthMultiplier(d) / 2) * graphProps.nodeRadius)
		.attr('y', (d) => -getRectNodeHeight(d, graphProps.nodeRadius) / 2)
		.attr('rx', (d) =>
			isBoardAnchorNode(d) || isPdfDocumentNode(d) ? 20 : d.pdfAnalysisId ? 14 : 16
		)
		.attr('ry', (d) =>
			isBoardAnchorNode(d) || isPdfDocumentNode(d) ? 20 : d.pdfAnalysisId ? 14 : 16
		);
	rectNode.select('circle.node').classed('hidden', true); // Hides the circle.
	rectNode.select('rect.node').classed('hidden', false); // Displays the rectangle.

	// Restore the circular token when a phrase is shortened back to a word.
	const circleNode = node.filter((d) => d.style !== 'rect');
	circleNode.select('circle.node').classed('hidden', false);
	circleNode.select('rect.node').classed('hidden', true);

	// Child `.menu` groups keep a stale datum after ShareDB rebinds the parent.
	node.each(function (d) {
		d3.select(this).select<SVGGElement>('g.menu').datum(d);
	});

	// During a hot reload, nodes created by the previous renderer can still
	// contain the old per-colour heart images. Keep those in sync until the
	// node is recreated, but do not run this legacy layout for new nodes.
	if (!node.select('g > image.heart').empty()) {
		// Get the colors assigned to board users.
		const USER_HEART_COLOURS = graphProps.boardUsers.map((u) => u.colour);

		// Loops through the user colors to position the heart icons for each color.
		USER_HEART_COLOURS.forEach((colourClass, index) => {
			const colourProps = HEART_POSITIONS[index]; // Gets heart position coordinates based on index.

			// Selects the heart icon for the current color class.
			const hearts = node.select(`g > image.${colourClass}`);

			// Toggles the visibility of hearts based on whether the node has 'likes' for the color class.
			hearts.each(function (d: NCNode) {
				const el = this as unknown as HTMLElement;
				el.classList.toggle('hidden', !d.likes || !d.likes[colourClass]);
			});

			// Position hearts relative to the rect bottom so they stay pinned as the
			// node grows; circles keep their existing radial positions.
			const heartsNotHidden = hearts.filter((d) => (d.likes ? d.likes[colourClass] : false));
			heartsNotHidden
				.filter((d) => d.style === 'rect')
				.attr('x', index * 18 - 120)
				.attr('y', (d) => getRectNodeHeight(d, graphProps.nodeRadius) / 2 - 20);
			const imageHeartPosition = getImageCardHeartPosition(index);
			heartsNotHidden
				.filter(isMediaCardNode)
				.attr('x', imageHeartPosition.x)
				.attr('y', imageHeartPosition.y);

			heartsNotHidden
				.filter((d) => d.style !== 'rect' && !isMediaCardNode(d))
				.attr('x', graphProps.nodeRadius > 50 ? colourProps.x : colourProps.x / 1.6) // Adjust x position for circular nodes based on node radius.
				.attr('y', graphProps.nodeRadius > 50 ? colourProps.y : colourProps.y / 1.6); // Adjust y position for circular nodes.
		});

		// Filters out heart colors that are not used by the current board users.
		const NOT_USED_COLOURS = HEART_COLOURS.filter((colour) => !USER_HEART_COLOURS.includes(colour));
		const userCount = USER_HEART_COLOURS.length; // Gets the count of users.

		// Loops through unused colors and positions their hearts.
		NOT_USED_COLOURS.forEach((colourClass, index) => {
			const colourProps = HEART_POSITIONS.slice(USER_HEART_COLOURS.length)[index]; // Gets heart positions for unused colors.

			// Selects the heart icon for the current unused color class.
			const hearts = node.select(`g > image.${colourClass}`);

			// Toggles the visibility of hearts based on whether the node has 'likes' for the unused color class.
			hearts.each(function (d: NCNode) {
				const el = this as unknown as HTMLElement;
				el.classList.toggle('hidden', !d.likes || !d.likes[colourClass]);
			});

			const heartsNotHidden = hearts.filter((d) => (d.likes ? d.likes[colourClass] : false));
			heartsNotHidden
				.filter((d) => d.style === 'rect')
				.attr('x', (userCount + index > 14 ? index : userCount + index) * 18 - 120)
				.attr(
					'y',
					(d) =>
						getRectNodeHeight(d, graphProps.nodeRadius) / 2 - (userCount + index > 14 ? 36 : 20)
				);
			const imageHeartPosition = getImageCardHeartPosition(userCount + index);
			heartsNotHidden
				.filter(isMediaCardNode)
				.attr('x', imageHeartPosition.x)
				.attr('y', imageHeartPosition.y);

			heartsNotHidden
				.filter((d) => d.style !== 'rect' && !isMediaCardNode(d))
				.attr('x', graphProps.nodeRadius > 50 ? colourProps.x : colourProps.x / 1.6) // Adjust x position for circular nodes based on node radius.
				.attr('y', graphProps.nodeRadius > 50 ? colourProps.y : colourProps.y / 1.6); // Adjust y position for circular nodes.
		});
	}

	// Rich PDF and positioning-card bodies are refreshed by updateLabelInput.
	// Rebuilding them again here makes every menu update pay for the entire
	// board twice, even though only the active toolbar changed.
	updateNodeLikeControls(node, graphProps);
	updateBadgeAndLink(node, graphProps.nodeRadius);

	// Updates the text for the "like" action based on whether the current user has liked the node.
	node
		.select("[data-action='like'] tspan")
		.text((d) => getNodeLikeActionLabel(d, graphProps.currentUser));

	// Updates the text for the "merge" action based on whether the node is merged.
	node.select("[data-action='merge'] tspan").text((d) => (d.merged ? 'Unmerge' : 'Merge'));
};

const appendLabelInput = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	graphMode: GraphMode
): d3.Selection<HTMLTextAreaElement, NCNode, SVGGElement, null> => {
	const browser = detectBrowser();
	const textArea = node
		.append('foreignObject')
		.append<HTMLAnchorElement>('xhtml:a')
		.classed('node-link', true)
		.attr('target', '_blank')
		.attr('href', (d) => d.href || null)
		.append<HTMLTextAreaElement>('xhtml:textarea')
		.classed('input', true)
		.classed('node-input', true)
		.classed('overflow-hidden', browser === 'Safari')
		.classed('inversed', (d) => shouldUseInversedNodeText(d, graphMode))
		.attr('id', (d) => d.uid)
		.attr('type', 'text')
		.attr('autofocus', true)
		.attr('placeholder', 'Enter label');
	updateLabelInput(node, nodeRadius);
	updateInputSizesToFit(node, nodeRadius);
	return textArea;
};

// export const updateLabelInput = (
// 	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
// 	nodeRadius: number
// ): void => {
// 	node
// 		.select('g > foreignObject:not(.node-image):not(.node-video)')
// 		.attr('y', (d) => {
// 			const numLines = getNumberLines(d.label || '', nodeRadius, d.style === 'rect');
// 			if (d.style === 'rect') {
// 				if (numLines > 8) {
// 					return -8 * numLines - 5;
// 				}
// 				return -8 * numLines - 10;
// 			}
// 			return -8 * numLines - 6;
// 		})
// 		.attr('height', (d) => getInputHeight(d.label || '', nodeRadius, d.style === 'rect'))
// 		.select<HTMLTextAreaElement>('.input')
// 		.style(
// 			'min-height',
// 			(d) => `${getInputHeight(d.label || '', nodeRadius, d.style === 'rect')}px`
// 		)
// 		.attr('rows', (d) => getNumberLines(d.label || '', nodeRadius, d.style === 'rect'))
// 		.attr('title', (d) => (d.href ? 'Open link in new tab' : d.label || ''))
// 		.style('font-size', (d) => getFontSize(d, nodeRadius))
// 		.each(function (d) {
// 			this.innerText = d.label || '';
// 			this.value = d.label || '';
// 		});
// };

// Focus-preserving input update. For rect nodes this measures the textarea's
// natural `scrollHeight` and resizes the background rect, the foreignObject,
// and the textarea so the inner scrollbar only appears once the label
// genuinely exceeds MAX_RECT_HEIGHT.
export const updateLabelInput = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number
) => {
	nodes.each(function (d) {
		const node = d3.select(this);
		const input = node.select<HTMLTextAreaElement>('.input');
		const inputNode = input.node();
		const wasFocused = document.activeElement === inputNode;
		const cursorPos = inputNode?.selectionStart;
		const scrollPos = inputNode?.scrollTop;

		const newValue = d.label || '';
		const isRect = d.style === 'rect';
		const numLines = getNumberLines(newValue, nodeRadius, isRect);
		const labelForeignObject = node.select<SVGForeignObjectElement>(
			'g > foreignObject:not(.node-image):not(.node-video):not(.node-meta):not(.node-pdf-card-shell):not(.node-synthesise-header-shell):not(.node-positioning-card-shell):not(.node-grounding-card-shell)'
		);

		if (isPdfDocumentNode(d)) {
			writeMeasuredRectHeight(d, PDF_DOCUMENT_CARD_HEIGHT);
			labelForeignObject.classed('hidden', true);
			node
				.select('rect.node')
				.attr('height', PDF_DOCUMENT_CARD_HEIGHT)
				.attr('y', -PDF_DOCUMENT_CARD_HEIGHT / 2);
			updatePdfDocumentCardForNode(node, d, nodeRadius);
			updateBadgeAndLinkForNode(node, d, nodeRadius);
			updateSynthesiseOutputHeaderForNode(node, d, nodeRadius);
			return;
		}
		if (isPositioningOutputNode(d)) {
			const cardHeight = getPositioningCardHeight(d);
			writeMeasuredRectHeight(d, cardHeight);
			labelForeignObject.classed('hidden', true);
			node
				.select('rect.node')
				.attr('height', cardHeight)
				.attr('y', -cardHeight / 2);
			updatePositioningOutputCardForNode(node, d, nodeRadius);
			updateBadgeAndLinkForNode(node, d, nodeRadius);
			return;
		}
		if (isGroundingEvidenceCard(d)) {
			const cardHeight = getGroundingEvidenceCardHeight(d);
			writeMeasuredRectHeight(d, cardHeight);
			labelForeignObject.classed('hidden', true);
			node.select('rect.node').attr('height', cardHeight).attr('y', -cardHeight / 2);
			updateGroundingEvidenceCardForNode(node, d, nodeRadius);
			updateBadgeAndLinkForNode(node, d, nodeRadius);
			return;
		}

		labelForeignObject.classed('hidden', false);

		if (isRect) {
			const innerForeignObject = labelForeignObject;
			// Width is deterministic from nodeRadius; set it before measuring so
			// the browser word-wraps with the same width the rendered textarea
			// will use.
			const inputWidth = getRectInputContentWidth(nodeRadius, d);
			innerForeignObject.attr('x', -inputWidth / 2).attr('width', inputWidth);

			if (inputNode) {
				if (inputNode.value !== newValue) {
					inputNode.value = newValue;
				}
				// Apply final font-size before measuring so scrollHeight reflects
				// the actual rendered text, not the previous (or default) size.
				inputNode.style.fontSize = getFontSize(d, nodeRadius);
				const { outer, inner } = measureRectHeightsForTextarea(
					inputNode,
					d.synthesiseRecipe ? SYNTHESISE_OUTPUT_MAX_HEIGHT : MAX_RECT_HEIGHT
				);
				writeMeasuredRectHeight(d, outer);

				const inputY = computeRectInputY(outer, inner);
				node
					.select('rect.node')
					.attr('height', outer)
					.attr('y', -outer / 2);
				innerForeignObject.attr('y', inputY).attr('height', inner);
				input
					.style('height', `${inner}px`)
					.style('min-height', `${inner}px`)
					.style('max-height', `${inner}px`);
			}
		} else {
			labelForeignObject
				.attr('y', -8 * numLines - 6)
				.attr('height', getInputHeight(newValue, nodeRadius, isRect));
		}

		// For rect nodes, rows is intentionally left at the measurement's value
		// (1) since the explicit `style.height` drives the final size; setting
		// it back to numLines would re-introduce the phantom-row whitespace.
		input
			.text(newValue)
			.attr('rows', isRect ? 1 : numLines)
			.style('font-size', getFontSize(d, nodeRadius));

		updateBadgeAndLinkForNode(node, d, nodeRadius);
		updateSynthesiseOutputHeaderForNode(node, d, nodeRadius);
		updateExpandableTextControlForNode(node, d, nodeRadius);

		// Restore focus and state with multiple fallback strategies
		if (wasFocused && inputNode) {
			const restoreFocus = () => {
				if (inputNode && document.activeElement !== inputNode) {
					inputNode.focus();
					if (cursorPos !== undefined) {
						inputNode.setSelectionRange(cursorPos, cursorPos);
						inputNode.scrollTop = scrollPos || 0;
					}
				}
			};

			// Strategy 1: Immediate restoration (for fast updates)
			restoreFocus();

			// Strategy 2: requestAnimationFrame (for normal framerates)
			requestAnimationFrame(restoreFocus);

			// Strategy 3: setTimeout with 0ms (runs after current call stack, more reliable than RAF)
			setTimeout(restoreFocus, 0);

			// Strategy 4: Additional setTimeout with 1ms (catches cases where 0ms isn't enough)
			setTimeout(restoreFocus, 1);

			// Strategy 5: requestAnimationFrame with double buffering (for very low framerates)
			requestAnimationFrame(() => {
				requestAnimationFrame(restoreFocus);
			});
		}
	});
};

const updateInputSizesToFit = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number
): void => {
	nodes
		.select(
			'g.menu-open > foreignObject:not(.node-image):not(.node-video):not(.node-meta):not(.node-pdf-card-shell):not(.node-synthesise-header-shell):not(.node-positioning-card-shell)'
		)
		.attr('x', (d) =>
			d.style === 'rect' ? -getRectInputContentWidth(nodeRadius, d) / 2 : -nodeRadius * 1.21 + 6
		)
		.attr('width', (d) =>
			d.style === 'rect' ? getRectInputContentWidth(nodeRadius, d) : 2.4 * nodeRadius - 10
		);
	nodes
		.select(
			'g:not(.menu-open) > foreignObject:not(.node-image):not(.node-video):not(.node-meta):not(.node-pdf-card-shell):not(.node-synthesise-header-shell):not(.node-positioning-card-shell)'
		)
		.attr('x', (d) =>
			d.style === 'rect' ? -getRectInputContentWidth(nodeRadius, d) / 2 : -nodeRadius + 8
		)
		.attr('width', (d) =>
			d.style === 'rect' ? getRectInputContentWidth(nodeRadius, d) : 2 * nodeRadius - 16
		);
};

const stopPropagation = (event: Event) => {
	event.stopPropagation();
};

const PDF_EXPAND_SVG =
	'<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="m21 3-7 7"/><path d="m3 21 7-7"/></svg>';

const resolveMediaPlaybackUrl = (src: string | undefined, kind: 'image' | 'video'): string | null => {
	if (!src) return null;
	if (src.startsWith('http') && !src.includes(window.location.host)) {
		const endpoint = kind === 'video' ? '/export-videos' : '/export-images';
		return `${endpoint}?responseType=blob&url=${encodeURIComponent(src)}`;
	}
	return src;
};

const appendMediaExpandButton = (
	parent: d3.Selection<HTMLDivElement, NCNode, SVGGElement, null>,
	openMediaContent: (node: NCNode) => void,
	kind: 'image' | 'video'
) => {
	parent
		.append('xhtml:button')
		.attr('type', 'button')
		.attr('class', 'node-media-card__expand')
		.attr('aria-label', (d) => `View ${kind} larger`)
		.attr('title', 'View larger')
		.html(PDF_EXPAND_SVG)
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			openMediaContent(d);
		});
};

const appendNodeImg = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	activateNode: (node: NCNode) => void,
	openMediaContent: (node: NCNode) => void
) => {
	const card = node
		.append('foreignObject')
		.attr('x', -IMAGE_CARD_WIDTH / 2)
		.attr('y', -IMAGE_CARD_HEIGHT / 2)
		.attr('width', IMAGE_CARD_WIDTH)
		.attr('height', IMAGE_CARD_HEIGHT)
		.attr('class', 'node-image')
		.append<HTMLDivElement>('xhtml:div')
		.attr('class', 'img-div')
		.attr('id', (d) => d.uid)
		// foreignObject descendants do not reliably inherit the SVG node cursor.
		// Make media bodies explicitly discoverable and draggable in Synthesise.
		.style('cursor', 'pointer')
		.on('click', (event: MouseEvent, d: NCNode) => {
			// Mirror PDF/positioning cards: foreignObject HTML clicks often do not
			// resolve correctly via the svg-level getNodeFromCircle path.
			event.stopPropagation();
			activateNode(d);
		});
	card
		.append('xhtml:div')
		.attr('class', 'node-media-card__preview')
		.style('background-image', (d) => {
			const url = resolveMediaPlaybackUrl(d.src, 'image');
			return url ? `url(${url})` : null;
		});
	card
		.append('xhtml:div')
		.attr('class', 'node-media-card__caption')
		.append('xhtml:span')
		.attr('class', 'node-media-card__caption-text')
		.text((d) => d.label || 'Visual reference');
	appendMediaExpandButton(card, openMediaContent, 'image');
};

const appendNodeVideo = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	activateNode: (node: NCNode) => void,
	openMediaContent: (node: NCNode) => void
) => {
	const card = node
		.append('foreignObject')
		.attr('x', -IMAGE_CARD_WIDTH / 2)
		.attr('y', -IMAGE_CARD_HEIGHT / 2)
		.attr('width', IMAGE_CARD_WIDTH)
		.attr('height', IMAGE_CARD_HEIGHT)
		.attr('class', 'node-video')
		.append<HTMLDivElement>('xhtml:div')
		.attr('class', 'video-div')
		.attr('id', (d) => d.uid)
		.style('cursor', 'pointer')
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			activateNode(d);
		});

	card
		.append('xhtml:video')
		.attr('width', IMAGE_CARD_WIDTH)
		.attr('height', MEDIA_CARD_PREVIEW_HEIGHT)
		.attr('muted', 'muted')
		.attr('playsinline', 'playsinline')
		.attr('preload', 'metadata')
		.attr('src', (d) => resolveMediaPlaybackUrl(d.src, 'video'))
		.attr('type', 'video/mp4');
	card
		.append('xhtml:div')
		.attr('class', 'node-media-card__caption')
		.append('xhtml:span')
		.attr('class', 'node-media-card__caption-text')
		.text((d) => d.label || 'Visual reference');

	appendMediaExpandButton(card, openMediaContent, 'video');
};

// External-link glyph inlined so we don't need a new SVG asset; sized to 12x12
// so it sits neatly in the 18x18 link button.
const EXTERNAL_LINK_SVG =
	'<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3h7v7"/><path d="M10 14L21 3"/><path d="M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6"/></svg>';

const updatePdfDocumentCardForNode = (
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const foreignObject = node.select<SVGForeignObjectElement>('foreignObject.node-pdf-card-shell');
	if (foreignObject.empty()) return;

	if (!isPdfDocumentNode(d)) {
		foreignObject.classed('hidden', true);
		return;
	}

	const width = getRectWidthMultiplier(d) * nodeRadius;
	foreignObject
		.classed('hidden', false)
		.attr('x', -width / 2)
		.attr('y', -PDF_DOCUMENT_CARD_HEIGHT / 2)
		.attr('width', width)
		.attr('height', PDF_DOCUMENT_CARD_HEIGHT);
	foreignObject.select<HTMLElement>('.pdf-node-card__title').text(getPdfDisplayName(d));
	foreignObject
		.select<HTMLElement>('.pdf-node-card__summary')
		.text(d.label || 'No summary available.');
	foreignObject
		.select<HTMLButtonElement>('.pdf-node-card__expand')
		.attr('aria-label', `Open full summary for ${getPdfDisplayName(d)}`)
		.attr('title', 'Open full PDF summary');
};

const updatePdfDocumentCards = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number
): void => {
	nodes.each(function (d) {
		updatePdfDocumentCardForNode(d3.select(this), d, nodeRadius);
	});
};

const appendPdfDocumentCard = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	activateNode: (node: NCNode) => void,
	openPdfDocument: (node: NCNode) => void
): void => {
	const foreignObject = nodes.append('foreignObject').attr('class', 'node-pdf-card-shell hidden');
	const card = foreignObject
		.append<HTMLDivElement>('xhtml:div')
		.attr('class', 'pdf-node-card')
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			activateNode(d);
		});

	const header = card.append<HTMLDivElement>('xhtml:div').attr('class', 'pdf-node-card__header');
	header
		.append<HTMLSpanElement>('xhtml:span')
		.attr('class', 'pdf-node-card__document-icon')
		.attr('aria-hidden', 'true')
		.text('PDF');
	header.append<HTMLSpanElement>('xhtml:span').attr('class', 'pdf-node-card__title');
	header
		.append<HTMLButtonElement>('xhtml:button')
		.attr('class', 'pdf-node-card__expand')
		.attr('type', 'button')
		.html(PDF_EXPAND_SVG)
		.on('mousedown', stopPropagation)
		.on('pointerdown', stopPropagation)
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			openPdfDocument(d);
		});
	card.append<HTMLParagraphElement>('xhtml:p').attr('class', 'pdf-node-card__summary');

	updatePdfDocumentCards(nodes, nodeRadius);
};

const compactSocialCount = (value: number): string =>
	new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const updateGroundingEvidenceCardForNode = (
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const shell = node.select<SVGForeignObjectElement>('foreignObject.node-grounding-card-shell');
	const evidence = d.groundingEvidence;
	if (shell.empty()) return;
	if (!evidence || !isGroundingEvidenceCard(d)) {
		shell.classed('hidden', true);
		return;
	}
	const height = getGroundingEvidenceCardHeight(d);
	const width = getRectWidthMultiplier(d) * nodeRadius;
	shell
		.classed('hidden', false)
		.attr('x', -width / 2)
		.attr('y', -height / 2)
		.attr('width', width)
		.attr('height', height);
	const card = shell.select<HTMLElement>('.grounding-evidence-card');
	card.classed('is-inspire-origin', evidence.originBoard === 'inspire');
	const hasMedia = Boolean(evidence.image);
	card.select<HTMLElement>('.grounding-evidence-card__media-wrap')
		.classed('hidden', !hasMedia)
		.style('display', hasMedia ? 'block' : 'none');
	const media = card.select<HTMLImageElement>('.grounding-evidence-card__media');
	media.classed('hidden', !hasMedia).attr('src', evidence.image || '').attr('alt', evidence.isVideo ? 'Video preview' : 'Social post image');
	card.select<HTMLElement>('.grounding-evidence-card__video').classed('hidden', !evidence.isVideo);
	card.select<HTMLElement>('.grounding-evidence-card__content').text(
		(evidence.content || evidence.title || evidence.detail).slice(0, 240)
	).style('margin', hasMedia ? '9px 11px 5px' : '43px 11px 5px');
	card.select<HTMLElement>('.grounding-evidence-card__platform').text([
		evidence.platform || evidence.source.replace(/^social-profile:/, '') || 'Context',
		evidence.publishedAt ? new Date(evidence.publishedAt).toLocaleDateString() : '',
	].filter(Boolean).join(' · '));
	card.select<HTMLElement>('.grounding-evidence-card__likes').text(
		typeof evidence.engagement?.likes === 'number' ? `${compactSocialCount(evidence.engagement.likes)} likes` : ''
	);
	const platformLabel: Record<string, string> = { instagram: 'IG', linkedin: 'LinkedIn', x: 'X', twitter: 'X' };
	card.select<HTMLElement>('.grounding-evidence-card__source').text(
		platformLabel[(evidence.platform || '').toLowerCase()] || evidence.platform || 'Social'
	);
	const pills = card.select<HTMLElement>('.grounding-evidence-card__pills');
	pills.selectAll('*').remove();
	[[evidence.sentiment, 'sentiment'], [evidence.stance, 'stance'], ...(evidence.emotions || []).slice(0, 1).map((emotion) => [emotion, 'emotion'])]
		.filter(([value]) => Boolean(value))
		.forEach(([value, kind]) => pills.append<HTMLSpanElement>('xhtml:span')
			.attr('class', `grounding-evidence-card__pill is-${kind} is-${String(value).toLowerCase()}`)
			.style('padding', '2px 6px').style('border-radius', '999px').style('background', kind === 'stance' ? '#e5edf9' : '#f1edf8').style('font-size', '9px').style('font-weight', '700').text(String(value)));
};

const appendGroundingEvidenceCard = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	activateNode: (node: NCNode) => void,
	openNodeContent: (node: NCNode) => void
): void => {
	const shell = nodes.append('foreignObject').attr('class', 'node-grounding-card-shell hidden');
	const card = shell.append<HTMLDivElement>('xhtml:article').attr('class', 'grounding-evidence-card')
		.style('box-sizing', 'border-box').style('position', 'relative').style('width', '100%').style('height', '100%')
		.style('overflow', 'hidden').style('border', '1px solid #d9d7e6').style('border-radius', '16px')
		.style('background', '#fff').style('box-shadow', '0 7px 18px rgba(39,38,61,.12)').style('cursor', 'pointer')
		.on('click', (event: MouseEvent, node: NCNode) => { event.stopPropagation(); activateNode(node); });
	card.append<HTMLSpanElement>('xhtml:span').attr('class', 'grounding-evidence-card__source')
		.style('position', 'absolute').style('top', '8px').style('left', '10px').style('padding', '4px 8px').style('border-radius', '999px').style('background', '#ecebf3').style('font-size', '10px').style('font-weight', '800');
	const media = card.append<HTMLDivElement>('xhtml:div').attr('class', 'grounding-evidence-card__media-wrap').style('position', 'relative').style('height', '104px').style('margin-top', '36px').style('background', '#ebeaf1');
	media.append<HTMLImageElement>('xhtml:img').attr('class', 'grounding-evidence-card__media').style('display', 'block').style('width', '100%').style('height', '100%').style('object-fit', 'cover');
	media.append<HTMLSpanElement>('xhtml:span').attr('class', 'grounding-evidence-card__video hidden').text('▶')
		.style('position', 'absolute').style('right', '8px').style('bottom', '8px').style('padding', '4px 7px').style('border-radius', '999px').style('background', 'rgba(24,24,34,.78)').style('color', '#fff').style('font-size', '12px');
	card.append<HTMLParagraphElement>('xhtml:p').attr('class', 'grounding-evidence-card__content').style('height', '49px').style('overflow', 'hidden').style('margin', '9px 11px 5px').style('font-size', '12px').style('font-weight', '600').style('line-height', '1.35');
	const meta = card.append<HTMLDivElement>('xhtml:div').attr('class', 'grounding-evidence-card__meta').style('display', 'flex').style('justify-content', 'space-between').style('margin', '0 11px').style('font-size', '10px');
	meta.append<HTMLSpanElement>('xhtml:span').attr('class', 'grounding-evidence-card__platform');
	meta.append<HTMLSpanElement>('xhtml:span').attr('class', 'grounding-evidence-card__likes');
	card.append<HTMLDivElement>('xhtml:div').attr('class', 'grounding-evidence-card__pills').style('display', 'flex').style('flex-wrap', 'wrap').style('gap', '4px').style('margin', '7px 11px');
	card.append<HTMLButtonElement>('xhtml:button').attr('class', 'grounding-evidence-card__expand').attr('type', 'button').html(PDF_EXPAND_SVG)
		// The SVG like control is wider than its heart icon; reserve its full width
		// plus a visible gap so both header actions remain independently clickable.
		.style('position', 'absolute').style('top', '5px').style('right', '58px').style('width', '27px').style('height', '27px').style('border', '0').style('border-radius', '7px').style('background', '#f4f3f8').style('cursor', 'pointer')
		.on('mousedown', stopPropagation).on('pointerdown', stopPropagation)
		.on('click', (event: MouseEvent, node: NCNode) => { event.stopPropagation(); openNodeContent(node); });
	nodes.each(function (d) { updateGroundingEvidenceCardForNode(d3.select(this), d, nodeRadius); });
};

const updatePositioningOutputCardForNode = (
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const shell = node.select<SVGForeignObjectElement>('foreignObject.node-positioning-card-shell');
	const output = d.positioningOutput
		? toReadablePositioningOutput(d.positioningOutput)
		: undefined;
	if (shell.empty()) return;
	if (!output || !isPositioningOutputNode(d)) {
		shell.classed('hidden', true);
		return;
	}
	const width = getRectWidthMultiplier(d) * nodeRadius;
	const height = getPositioningCardHeight(d);
	shell
		.classed('hidden', false)
		.attr('x', -width / 2)
		.attr('y', -height / 2)
		.attr('width', width)
		.attr('height', height);
	const card = shell.select<HTMLElement>('.positioning-board-card');
	card.attr('data-output-type', output.type);
	card.select<HTMLElement>('.positioning-board-card__eyebrow').text(output.eyebrow);
	card.select<HTMLElement>('.positioning-board-card__title').text(output.title);
	card.select<HTMLElement>('.positioning-board-card__subtitle').text(output.subtitle || '');
	const visibleSummary =
		output.summary ||
		output.details.find((detail) => detail.value.trim() && detail.value !== output.title)?.value ||
		'';
	card
		.select<HTMLElement>('.positioning-board-card__summary')
		.classed('hidden', !visibleSummary)
		.text(visibleSummary);
	card
		.select<HTMLElement>('.positioning-board-card__version')
		.text(`Idea Box snapshot · v${output.version}`);
	const visual = card.select<HTMLElement>('.positioning-board-card__visual');
	visual.selectAll('*').remove();
	visual
		.classed('hidden', false)
		.classed('is-palette', output.payload.kind === 'palette')
		.classed('is-monogram', false);
	if (output.payload.kind === 'palette') {
		output.payload.palette.colours.slice(0, 6).forEach((colour) => {
			visual
				.append<HTMLSpanElement>('xhtml:span')
				.style('background-color', colour.hex)
				.attr('title', `${colour.name} · ${colour.hex}`);
		});
	} else if (output.imageUrl || (output.payload.kind === 'media' && !output.payload.isVideo)) {
		visual
			.append<HTMLImageElement>('xhtml:img')
			.attr('src', output.imageUrl || (output.payload.kind === 'media' ? output.payload.url : ''))
			.attr('alt', '');
	} else if (output.type === 'persona' || output.type === 'character') {
		const initials = output.title
			.split(/\s+/)
			.slice(0, 2)
			.map((part) => part[0]?.toUpperCase())
			.join('');
		visual
			.classed('is-monogram', true)
			.append<HTMLSpanElement>('xhtml:span')
			.text(initials || (output.type === 'persona' ? 'P' : 'C'));
	} else {
		visual.classed('hidden', true);
	}
	const details = card.select<HTMLElement>('.positioning-board-card__details');
	details.selectAll('*').remove();
	output.details
		.filter(
			(detail) =>
				detail.value.trim() &&
				detail.value.trim() !== visibleSummary.trim() &&
				detail.value.trim() !== output.title.trim()
		)
		.slice(0, 2)
		.forEach((detail) => {
			const row = details.append<HTMLDivElement>('xhtml:div');
			row.append<HTMLSpanElement>('xhtml:span').text(detail.title);
			row.append<HTMLParagraphElement>('xhtml:p').text(detail.value);
		});
};

const appendPositioningOutputCard = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	activateNode: (node: NCNode) => void,
	openNodeContent: (node: NCNode) => void
): void => {
	const shell = nodes.append('foreignObject').attr('class', 'node-positioning-card-shell hidden');
	const card = shell
		.append<HTMLDivElement>('xhtml:article')
		.attr('class', 'positioning-board-card')
		.on('click', (event: MouseEvent, node: NCNode) => {
			event.stopPropagation();
			activateNode(node);
		});
	const header = card.append<HTMLDivElement>('xhtml:header');
	const headings = header.append<HTMLDivElement>('xhtml:div');
	headings.append<HTMLSpanElement>('xhtml:span').attr('class', 'positioning-board-card__eyebrow');
	headings.append<HTMLHeadingElement>('xhtml:h3').attr('class', 'positioning-board-card__title');
	headings
		.append<HTMLParagraphElement>('xhtml:p')
		.attr('class', 'positioning-board-card__subtitle');
	header
		.append<HTMLButtonElement>('xhtml:button')
		.attr('class', 'positioning-board-card__expand')
		.attr('type', 'button')
		.attr('title', 'Open full saved output')
		.html(PDF_EXPAND_SVG)
		.on('mousedown', stopPropagation)
		.on('pointerdown', stopPropagation)
		.on('click', (event: MouseEvent, node: NCNode) => {
			event.stopPropagation();
			openNodeContent(node);
		});
	card.append<HTMLDivElement>('xhtml:div').attr('class', 'positioning-board-card__visual');
	card.append<HTMLParagraphElement>('xhtml:p').attr('class', 'positioning-board-card__summary');
	card.append<HTMLDivElement>('xhtml:div').attr('class', 'positioning-board-card__details');
	card.append<HTMLSpanElement>('xhtml:span').attr('class', 'positioning-board-card__version');
	nodes.each(function (d) {
		updatePositioningOutputCardForNode(d3.select(this), d, nodeRadius);
	});
};

const updateSynthesiseOutputHeaderForNode = (
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const foreignObject = node.select<SVGForeignObjectElement>(
		'foreignObject.node-synthesise-header-shell'
	);
	if (foreignObject.empty()) return;

	if (!isTextSynthesiseOutputNode(d)) {
		foreignObject.classed('hidden', true);
		return;
	}

	const rectWidth = getRectWidthMultiplier(d) * nodeRadius;
	const rectHeight = getRectNodeHeight(d, nodeRadius);
	const sideInset = 8;
	const reservedLikeWidth = RECT_LIKE_CONTROL_WIDTH + RECT_META_LIKE_GAP;
	foreignObject
		.classed('hidden', false)
		.attr('x', -rectWidth / 2 + sideInset)
		.attr('y', -rectHeight / 2 + 7)
		.attr('width', rectWidth - sideInset * 2 - reservedLikeWidth)
		.attr('height', SYNTHESISE_HEADER_HEIGHT);
	foreignObject
		.select<HTMLElement>('.synthesise-output-header__task')
		.text(getSynthesiseOutputTitle(d.synthesiseRecipe));
	const outputTitle = getSynthesiseOutputTitle(d.synthesiseRecipe);
	foreignObject
		.select<HTMLButtonElement>('.synthesise-output-header__expand')
		.attr('aria-label', `Open full ${outputTitle}`)
		.attr('title', 'Open full generated output');
};

const appendSynthesiseOutputHeader = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	openNodeContent: (node: NCNode) => void
): void => {
	const foreignObject = nodes
		.append('foreignObject')
		.attr('class', 'node-synthesise-header-shell hidden');
	const header = foreignObject
		.append<HTMLDivElement>('xhtml:div')
		.attr('class', 'synthesise-output-header');
	header.append<HTMLSpanElement>('xhtml:span').attr('class', 'synthesise-output-header__task');
	header
		.append<HTMLButtonElement>('xhtml:button')
		.attr('class', 'synthesise-output-header__expand')
		.attr('type', 'button')
		.html(PDF_EXPAND_SVG)
		.on('mousedown', stopPropagation)
		.on('pointerdown', stopPropagation)
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			openNodeContent(d);
		});

	nodes.each(function (d) {
		updateSynthesiseOutputHeaderForNode(d3.select(this), d, nodeRadius);
	});
};

const isExpandableTextNode = (node: NCNode): boolean =>
	(Boolean(node.topics?.includes('expandable-text')) || Boolean(node.href)) &&
	!isGroundingEvidenceCard(node) &&
	!node.src &&
	!node.pdfAnalysisId &&
	!node.positioningOutput &&
	!node.synthesiseRecipe;

const updateExpandableTextControlForNode = (
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const foreignObject = node.select<SVGForeignObjectElement>(
		'foreignObject.node-expandable-text-shell'
	);
	if (foreignObject.empty()) return;
	if (!isExpandableTextNode(d)) {
		foreignObject.classed('hidden', true);
		return;
	}
	const isRect = d.style === 'rect';
	const width = isRect ? getRectWidthMultiplier(d) * nodeRadius : nodeRadius * 2;
	const height = isRect ? getRectNodeHeight(d, nodeRadius) : nodeRadius * 2;
	const isWebResult = isWebResultCardNode(d);
	const webResultExpandX =
		width / 2 -
		CARD_ACTION_RIGHT_INSET -
		RECT_LIKE_CONTROL_WIDTH -
		CARD_ACTION_GAP -
		RECT_EXPAND_CONTROL_WIDTH;
	foreignObject
		.classed('hidden', false)
		// Web results use the same header contract as social cards: source at
		// top-left, then expand and like in a padded top-right action rail.
		.attr('x', isWebResult ? webResultExpandX : -width / 2 + 4)
		.attr('y', isWebResult ? -height / 2 + 7 : -height / 2 + 2)
		.attr('width', RECT_EXPAND_CONTROL_WIDTH)
		.attr('height', 28);
	foreignObject
		.select<HTMLButtonElement>('.node-expandable-text__expand')
		.attr('aria-label', d.href ? `Open ${d.label || 'web result'} in this board` : `Open full text for ${d.label || 'node'}`)
		.attr('title', d.href ? 'Open page in board' : 'Open full text');
};

const appendExpandableTextControl = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number,
	openNodeContent: (node: NCNode) => void
): void => {
	const foreignObject = nodes
		.append('foreignObject')
		.attr('class', 'node-expandable-text-shell hidden');
	foreignObject
		.append<HTMLButtonElement>('xhtml:button')
		.attr('class', 'node-expandable-text__expand')
		.attr('type', 'button')
		.html(PDF_EXPAND_SVG)
		.on('mousedown', stopPropagation)
		.on('pointerdown', stopPropagation)
		.on('click', (event: MouseEvent, d: NCNode) => {
			event.stopPropagation();
			openNodeContent(d);
		});

	nodes.each(function (d) {
		updateExpandableTextControlForNode(d3.select(this), d, nodeRadius);
	});
};

/**
 * Appends a small foreignObject in the top-right of a node that holds the
 * origin badge and (when the node has an href) an external-link button. Created
 * for every node (so circle-to-rect transitions via `preservePhraseNode` still
 * get a badge) but hidden / collapsed when no origin and no href are present.
 */
const appendBadgeAndLink = (node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>): void => {
	const fo = node
		.append('foreignObject')
		.attr('class', 'node-meta')
		.attr('width', 160)
		.attr('height', RECT_BADGE_HEIGHT + 2);

	const container = fo.append<HTMLDivElement>('xhtml:div').attr('class', 'node-meta-content');

	container
		.append<HTMLSpanElement>('xhtml:span')
		.attr('class', 'node-origin-badge hidden')
		.on('click', stopPropagation)
		.on('mousedown', stopPropagation);

	container
		.append<HTMLAnchorElement>('xhtml:a')
		.attr('class', 'node-link-button hidden')
		.attr('target', '_blank')
		.attr('rel', 'noopener noreferrer')
		.attr('aria-label', 'Open source link')
		.on('click', stopPropagation)
		.on('mousedown', stopPropagation)
		.html(EXTERNAL_LINK_SVG);
};

const updateBadgeAndLinkForNode = (
	// Accept the less precise Selection that `d3.select(this)` yields inside
	// `nodes.each` callbacks; the caller still passes the typed datum explicitly.
	node: d3.Selection<SVGGElement, unknown, d3.BaseType, unknown>,
	d: NCNode,
	nodeRadius: number
): void => {
	const fo = node.select<SVGForeignObjectElement>('foreignObject.node-meta');
	if (fo.empty()) return;

	const isRect = d.style === 'rect';
	const derivedOrigin: NodeOrigin | null = isRect ? getNodeOrigin(d) : null;
	const origin =
		derivedOrigin && !nodeOriginRepeatsLabel(derivedOrigin, d.label) ? derivedOrigin : null;
	const showLink = isRect && Boolean(d.href);

	if (!isRect || isPdfDocumentNode(d) || isGroundingEvidenceCard(d) || (!origin && !showLink)) {
		fo.classed('hidden', true);
		return;
	}

	fo.classed('hidden', false);

	const rectHalfWidth = (getRectWidthMultiplier(d) * nodeRadius) / 2;
	const rectHalfHeight = getRectNodeHeight(d, nodeRadius) / 2;
	const isWebResult = isWebResultCardNode(d);
	const reservedHeaderActionWidth =
		RECT_LIKE_CONTROL_WIDTH +
		RECT_META_LIKE_GAP +
		(isWebResult ? RECT_EXPAND_CONTROL_WIDTH + CARD_ACTION_GAP : 0);
	const foWidth = Math.max(
		isWebResult ? 52 : 80,
		2 * rectHalfWidth - 2 * RECT_BADGE_INSET_X - reservedHeaderActionWidth
	);

	fo.attr('x', -rectHalfWidth + RECT_BADGE_INSET_X).attr('y', -rectHalfHeight + RECT_BADGE_INSET_Y);
	fo.attr('width', foWidth);
	fo.select<HTMLElement>('.node-meta-content').style('position', 'relative').style('display', 'block');

	const badge = fo.select<HTMLSpanElement>('span.node-origin-badge');
	const linkButton = fo.select<HTMLAnchorElement>('a.node-link-button');

	const sourceBadgeActsAsLink = showLink && !isWebResult;
	if (origin) {
		badge
			.classed('hidden', false)
			.attr('data-origin', origin.key)
			.text(`${origin.label.toUpperCase()}${sourceBadgeActsAsLink ? ' ↗' : ''}`)
			.attr('title', sourceBadgeActsAsLink ? 'Open source link' : null)
			.attr('role', sourceBadgeActsAsLink ? 'link' : null)
			.attr('tabindex', sourceBadgeActsAsLink ? 0 : null)
			.style('padding', '4px 8px')
			.style('border-radius', '999px')
			.style('font-size', '10px')
			.style('font-weight', '800')
			.style('position', 'absolute')
			.style('left', '0')
			.style('top', '0')
			.style('cursor', () => (sourceBadgeActsAsLink ? 'pointer' : null))
			.on('click', (event: MouseEvent) => {
				if (!sourceBadgeActsAsLink) return;
				event.stopPropagation();
				window.open(d.href!, '_blank', 'noopener,noreferrer');
			})
			.on('keydown', (event: KeyboardEvent) => {
				if (!sourceBadgeActsAsLink || (event.key !== 'Enter' && event.key !== ' ')) return;
				event.preventDefault();
				event.stopPropagation();
				window.open(d.href!, '_blank', 'noopener,noreferrer');
			});
	} else {
		badge
			.classed('hidden', true)
			.text('')
			.attr('title', null)
			.attr('role', null)
			.attr('tabindex', null)
			.on('click', null)
			.on('keydown', null);
	}

	// When an origin badge exists, make that pill the source action. This avoids
	// showing an external-link glyph that can be mistaken for a second expand
	// button next to the text expander on compact social/result cards.
	if (showLink && !origin) {
		linkButton
			.classed('hidden', false)
			.attr('href', d.href!)
			.attr('title', 'Open source link')
			.style('width', `${RECT_LINK_BUTTON_SIZE}px`)
			.style('height', `${RECT_LINK_BUTTON_SIZE}px`)
			.style('border-radius', '7px')
			.style('background', '#f4f3f8')
			.style('border', '1px solid #bfc1d4')
			.style('color', '#3946ae')
			.style('position', 'absolute')
			.style('right', '0')
			.style('top', '0');
	} else {
		linkButton.classed('hidden', true).attr('href', null);
	}
};

const updateBadgeAndLink = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	nodeRadius: number
): void => {
	nodes.each(function (d) {
		updateBadgeAndLinkForNode(d3.select(this), d, nodeRadius);
	});
};

const appendCircleOrRect = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	const isMediaVisualNode = (d: NCNode) => Boolean(d.src);
	node
		// .filter((d) => !d.style)
		.append('circle')
		.attr('class', (d) => (d.style !== 'rect' || isMediaVisualNode(d) ? 'node' : 'node hidden'))
		.attr('r', graphProps.isDeleteAnimated && !graphProps.automation ? 0 : graphProps.nodeRadius)
		// Set the persisted Empathise colour at enter time. Previously it was
		// applied only by updateNodeCircles' transition, which produced a white
		// flash when Synthesise first rendered shared nodes.
		.attr('fill', (d) => (d.nodeColour ? hexOrNamedColour(d.nodeColour) : '#fff'))
		.attr('stroke', graphProps.userColourHex)
		.call((enter) => enter.transition().attr('r', graphProps.nodeRadius));

	node
		// .filter((d) => d.style === 'rect')
		.append('rect')
		.attr('class', (d) => (d.style === 'rect' && !isMediaVisualNode(d) ? 'node' : 'node hidden'))
		.attr('width', (d) => getRectWidthMultiplier(d) * graphProps.nodeRadius)
		.attr('height', (d) => getRectNodeHeight(d, graphProps.nodeRadius))
		.attr('rx', 5)
		.attr('ry', 5)
		.attr('x', (d) => -(getRectWidthMultiplier(d) / 2) * graphProps.nodeRadius)
		.attr('y', (d) => -getRectNodeHeight(d, graphProps.nodeRadius) / 2)
		.attr('fill', (d) => (d.nodeColour ? hexOrNamedColour(d.nodeColour) : '#fff'))
		.attr('stroke', graphProps.userColourHex)
		.call((enter) => enter.transition().attr('r', graphProps.nodeRadius));
};

const appendDeleteButton = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	// Remove from all nodes
	nodes.filter('g').selectAll('.delete').remove();
	// Add delete to node with open menu
	nodes
		.filter('g.menu-open')
		.filter((d) => isNodeEditable(d, graphProps))
		.append('image')
		.attr('class', 'delete')
		.attr('href', `/assets/graph/delete.svg`)
		.attr('x', (d) =>
			isMediaCardNode(d)
				? IMAGE_CARD_WIDTH / 2 - 24
				: d.style === 'rect'
					? graphProps.nodeRadius * 1.55
					: -8
		)
		.attr('y', (d) =>
			isMediaCardNode(d)
				? IMAGE_CARD_HEIGHT / 2 - 24
				: d.style === 'rect'
					? getRectNodeHeight(d, graphProps.nodeRadius) / 2 - 26
					: 36
		)
		.attr('width', 16)
		.attr('height', 16);
};

const appendMoveConnected = (nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>): void => {
	// Open the menu which has the menu-open class
	nodes.selectAll<SVGGElement, NCNode>('.nodes > g.menu-open .menu').call((menuEl) => {
		const node = menuEl.node() && menuEl.datum();
		if (node && getLinkedNodes(node).length > 0) {
			menuEl
				.selectAll<SVGGElement, NCNode>('.move-connected')
				.data(menuEl.data())
				.enter()
				.append('g')
				.attr('fill', `#333`)
				.attr('class', 'move-connected')
				.attr('transform', 'translate(130, -140)')
				.attr('title', 'Drag to move this and connected nodes')
				.attr('width', 130)
				.attr('height', 28)
				.call((moveGroupEl) => {
					moveGroupEl
						.append('rect')
						.attr('fill', '#eee')
						.attr('width', 180)
						.attr('height', 28)
						.attr('rx', 5)
						.attr('ry', 5);
					moveGroupEl
						.append('image')
						.attr('href', '/assets/graph/move.svg')
						.attr('width', 16)
						.attr('height', 16)
						.attr('transform', 'translate(9, 6)');
					moveGroupEl
						.append('text')
						.text('Move connected nodes')
						.attr('transform', 'translate(36, 18)');
				});
		}
	});
};

const radialToolbarPie = d3.pie<MenuOption>().value(1);

const appendRadialToolbarButtons = (
	menuEl: d3.Selection<SVGGElement, NCNode, d3.BaseType, unknown>,
	filteredMenuOptions: MenuOption[],
	graphProps: GraphProps,
	innerRadius: number,
	outerRadius: number
): void => {
	// Drop leftover rect-side buttons when a phrase node becomes a circle again.
	menuEl.selectAll('.arc').remove();
	const arcs = menuEl
		.selectAll<SVGGElement, PieArcDatum<MenuOption>>('.arc')
		.data(radialToolbarPie(filteredMenuOptions))
		.enter()
		.append('g')
		.attr('class', 'arc')
		.attr('fill', `var(--${graphProps.userColourClass}-menu)`)
		.attr('data-action', (d) => d.data.label.toLowerCase());

	const nodeDatum = menuEl.node() ? menuEl.datum() : undefined;
	const merged = nodeDatum?.merged;

	const radialToolbarArc = d3
		.arc<PieArcDatum<MenuOption>>()
		.innerRadius(innerRadius)
		.outerRadius(outerRadius)
		.padAngle(0.02);

	arcs.append('path').attr('class', 'menu-button').attr('d', radialToolbarArc);
	arcs
		.append('image')
		.attr('href', (d) => `/assets/graph/${d.data.label.toLowerCase()}.svg`)
		.attr('transform', (d) => {
			const coords = radialToolbarArc.centroid(d);
			coords[0] = coords[0] - 6;
			coords[1] = coords[1] - 20;
			return `translate(${coords[0]}, ${coords[1]})`;
		})
		.attr('width', 16)
		.attr('height', 16);

	arcs
		.append('text')
		.attr('transform', (d) => {
			const coords = radialToolbarArc.centroid(d);
			coords[0] = coords[0] + 2;
			coords[1] = coords[1] + 16;
			return `translate(${coords[0]}, ${coords[1]})`;
		})
		.append('tspan')
		.text((d) => {
			if (d.data.label === 'Like' && nodeDatum) {
				return getNodeLikeActionLabel(nodeDatum, graphProps.currentUser);
			}
			if (d.data.label === 'Merge' && merged) {
				return 'Unmerge';
			}
			return d.data.label;
		});
};

const RECT_MENU_BUTTON_WIDTH = 118;
const RECT_MENU_BUTTON_HEIGHT = 38;
const RECT_MENU_BUTTON_GAP = 8;
const RECT_MENU_EDGE_GAP = 12;

type RectMenuSide = 'top' | 'right' | 'bottom' | 'left';
type CardMenuLayoutEntry = { side: RectMenuSide; slot: 0 | 1; label: string };

/** Fixed side placement for Inspire phrase-node tools (8 options, 2 per side). */
const RECT_MENU_LAYOUT: CardMenuLayoutEntry[] = [
	{ side: 'top', slot: 0, label: 'Spark' },
	{ side: 'top', slot: 1, label: 'Empathise' },
	{ side: 'right', slot: 0, label: 'Generate' },
	{ side: 'right', slot: 1, label: 'Expand' },
	{ side: 'bottom', slot: 0, label: 'Connect' },
	{ side: 'bottom', slot: 1, label: 'Merge' },
	{ side: 'left', slot: 0, label: 'Search' },
	{ side: 'left', slot: 1, label: 'Like' },
];

/** Image cards keep the essential visual actions close to the rectangular card edges. */
const IMAGE_CARD_MENU_LAYOUT: CardMenuLayoutEntry[] = [
	{ side: 'top', slot: 0, label: 'Search' },
	{ side: 'top', slot: 1, label: 'Connect' },
	{ side: 'bottom', slot: 0, label: 'Like' },
];

/** Synthesise text/phrase cards expose creation and curation actions. */
const SYNTHESISE_CARD_MENU_LAYOUT: CardMenuLayoutEntry[] = [
	{ side: 'top', slot: 0, label: 'Synthesise' },
	{ side: 'top', slot: 1, label: 'Note' },
	{ side: 'right', slot: 0, label: 'Share' },
	{ side: 'bottom', slot: 0, label: 'Connect' },
	{ side: 'bottom', slot: 1, label: 'Like' },
];

/** Image/video cards on Synthesise always expose Share to team. */
const SYNTHESISE_MEDIA_CARD_MENU_LAYOUT: CardMenuLayoutEntry[] = [
	{ side: 'top', slot: 0, label: 'Synthesise' },
	{ side: 'top', slot: 1, label: 'Note' },
	{ side: 'right', slot: 0, label: 'Share' },
	{ side: 'bottom', slot: 0, label: 'Connect' },
	{ side: 'bottom', slot: 1, label: 'Like' },
];

const readLiveRectMenuHalfSize = (
	menuEl: d3.Selection<SVGGElement, NCNode, d3.BaseType, unknown>,
	nodeDatum: NCNode,
	graphProps: GraphProps
): { halfW: number; halfH: number } => {
	if (isMediaCardNode(nodeDatum)) {
		return { halfW: IMAGE_CARD_WIDTH / 2, halfH: IMAGE_CARD_HEIGHT / 2 };
	}
	const parent = menuEl.node()?.parentNode as SVGGElement | null;
	const rect = parent?.querySelector<SVGRectElement>('rect.node');
	const width = rect ? Number(rect.getAttribute('width')) : NaN;
	const height = rect ? Number(rect.getAttribute('height')) : NaN;
	return {
		halfW:
			Number.isFinite(width) && width > 0
				? width / 2
				: (getRectWidthMultiplier(nodeDatum) * graphProps.nodeRadius) / 2,
		halfH:
			Number.isFinite(height) && height > 0
				? height / 2
				: getRectNodeHeight(nodeDatum, graphProps.nodeRadius) / 2,
	};
};

const getRectMenuButtonTransform = (
	side: RectMenuSide,
	slot: 0 | 1,
	halfW: number,
	halfH: number
): string => {
	const w = RECT_MENU_BUTTON_WIDTH;
	const h = RECT_MENU_BUTTON_HEIGHT;
	const gap = RECT_MENU_BUTTON_GAP;
	const edge = RECT_MENU_EDGE_GAP;

	if (side === 'top' || side === 'bottom') {
		const y = side === 'top' ? -halfH - edge - h : halfH + edge;
		const x = slot === 0 ? -(w + gap / 2) : gap / 2;
		return `translate(${x}, ${y})`;
	}

	const x = side === 'left' ? -halfW - edge - w : halfW + edge;
	const y = slot === 0 ? -(h + gap / 2) : gap / 2;
	return `translate(${x}, ${y})`;
};

const appendRectSideToolbarButtons = (
	menuEl: d3.Selection<SVGGElement, NCNode, d3.BaseType, unknown>,
	menuOptions: MenuOption[],
	graphProps: GraphProps,
	nodeDatum: NCNode,
	layout: CardMenuLayoutEntry[] = RECT_MENU_LAYOUT
): void => {
	const { halfW, halfH } = readLiveRectMenuHalfSize(menuEl, nodeDatum, graphProps);
	const optionsByLabel = new Map(menuOptions.map((option) => [option.label, option]));
	const entries = layout.flatMap((layoutEntry) => {
		const option = optionsByLabel.get(layoutEntry.label);
		return option ? [{ layout: layoutEntry, option }] : [];
	});

	const merged = nodeDatum.merged;

	// Radial pie buttons from the previous circle layout cannot be reused.
	menuEl.selectAll('.arc').remove();

	const buttons = menuEl
		.selectAll<SVGGElement, { layout: CardMenuLayoutEntry; option: MenuOption }>('.arc')
		.data(entries, (entry) => entry.option.label)
		.enter()
		.append('g')
		.attr('class', 'arc')
		.attr('fill', `var(--${graphProps.userColourClass}-menu)`)
		.attr('transform', (entry) =>
			getRectMenuButtonTransform(entry.layout.side, entry.layout.slot, halfW, halfH)
		)
		.attr('data-action', (entry) => entry.option.label.toLowerCase());

	buttons
		.append('rect')
		.attr('class', 'menu-rect')
		.attr('fill', 'inherit')
		.attr('rx', 5)
		.attr('ry', 5)
		.attr('width', RECT_MENU_BUTTON_WIDTH)
		.attr('height', RECT_MENU_BUTTON_HEIGHT);

	buttons
		.append('image')
		.attr('href', (entry) => `/assets/graph/${entry.option.label.toLowerCase()}.svg`)
		.attr('transform', 'translate(12, 11)')
		.attr('width', 16)
		.attr('height', 16);

	buttons
		.append('text')
		.attr('x', RECT_MENU_BUTTON_WIDTH / 2)
		.attr('y', 25)
		.attr('text-anchor', 'middle')
		.append('tspan')
		.text((entry) => {
			if (entry.option.label === 'Like') {
				return getNodeLikeActionLabel(nodeDatum, graphProps.currentUser);
			}
			if (entry.option.label === 'Merge' && merged) {
				return 'Unmerge';
			}
			return entry.option.label;
		});
};

const appendToolbarButtons = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	// Use the parent node's datum, not the child `.menu` group's. After ShareDB
	// rebinds the parent, the menu can still hold a stale copy without `style`.
	nodes.filter('.menu-open').each(function (d) {
		const menuEl = d3.select<SVGGElement, NCNode>(this).select<SVGGElement>('g.menu').datum(d);
		if (menuEl.empty()) {
			return;
		}

		if (d.style === 'rect' || isMediaCardNode(d)) {
			if (graphProps.graphMode === 'moodboard') {
				const layout = isMediaCardNode(d)
					? SYNTHESISE_MEDIA_CARD_MENU_LAYOUT
					: SYNTHESISE_CARD_MENU_LAYOUT;
				const synthesiseActions = new Set(layout.map(({ label }) => label));
				appendRectSideToolbarButtons(
					menuEl,
					graphProps.menuOptions.filter((option) => synthesiseActions.has(option.label)),
					graphProps,
					d,
					layout
				);
				return;
			}
			if (isMediaCardNode(d)) {
				const imageActions = new Set(IMAGE_CARD_MENU_LAYOUT.map(({ label }) => label));
				appendRectSideToolbarButtons(
					menuEl,
					graphProps.menuOptions.filter((option) => imageActions.has(option.label)),
					graphProps,
					d,
					IMAGE_CARD_MENU_LAYOUT
				);
				return;
			}
			const radialOptions = graphProps.menuOptions.filter((option) => option.radial);
			const menuOptions = radialOptions.length
				? radialOptions
				: graphProps.menuOptions.filter((option) => option.shortText || option.longText);
			appendRectSideToolbarButtons(menuEl, menuOptions, graphProps, d);
			return;
		}

		appendRadialToolbarButtons(
			menuEl,
			graphProps.menuOptions.filter((option) => option.shortText),
			graphProps,
			graphProps.nodeRadius * 1.25,
			graphProps.nodeRadius * 2.5
		);
	});
};

const appendNodeLikeControls = (
	node: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	const control = node.append('g').attr('class', 'node-like-control');
	const keepLikeGestureInsideControl = (event: Event): void => event.stopPropagation();
	control
		.on('pointerdown.like', keepLikeGestureInsideControl)
		.on('mousedown.like', keepLikeGestureInsideControl)
		.on('touchstart.like', keepLikeGestureInsideControl);
	control.append('title');
	control
		.append('rect')
		.attr('class', 'node-like-control__surface')
		.attr('width', RECT_LIKE_CONTROL_WIDTH)
		.attr('height', 26)
		.attr('rx', 13);
	const heartButton = control
		.append('g')
		.attr('class', 'node-like-heart-button')
		.attr('role', 'button')
		.attr('tabindex', 0)
		.attr('aria-label', 'Like node');
	heartButton
		.append('image')
		.attr('class', 'node-like-heart-icon')
		.attr('x', 5)
		.attr('y', 4)
		.attr('width', 18)
		.attr('height', 18);
	const countGroup = control
		.append('g')
		.attr('class', 'node-like-count-group')
		.attr('role', 'button')
		.attr('tabindex', 0)
		.attr('aria-label', 'Show who liked this node');
	countGroup
		.append('rect')
		.attr('class', 'node-like-count-hit')
		.attr('x', 24)
		.attr('width', 24)
		.attr('height', 26);
	countGroup
		.append('text')
		.attr('class', 'node-like-count')
		.attr('x', 35)
		.attr('y', 17)
		.attr('text-anchor', 'middle');
	countGroup
		.append('foreignObject')
		.attr('class', 'node-like-popover')
		.attr('x', -132)
		.attr('y', 30)
		.attr('width', 180)
		.attr('height', 190)
		.append('xhtml:div')
		.attr('class', 'node-like-popover-content');

	const togglePopover = function (event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		const currentControl = d3.select<SVGGElement, NCNode>(
			(this as SVGGElement).parentElement as unknown as SVGGElement
		);
		const shouldOpen = !currentControl.classed('is-open');
		d3.selectAll('.node-like-control.is-open').classed('is-open', false);
		currentControl.classed('is-open', shouldOpen);
		if (shouldOpen) {
			const nodeElement = currentControl.node()?.parentElement;
			nodeElement?.parentElement?.appendChild(nodeElement);
		}
	};
	countGroup.on('click', togglePopover).on('keydown', function (event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') togglePopover.call(this, event);
	});
	heartButton
		.on('click', (event: MouseEvent, datum) => {
			event.preventDefault();
			event.stopPropagation();
			graphProps.onToggleNodeLike(datum);
			updateNodeLikeControls(node, graphProps);
		})
		.on('keydown', (event: KeyboardEvent, datum) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			event.stopPropagation();
			graphProps.onToggleNodeLike(datum);
			updateNodeLikeControls(node, graphProps);
		});
	updateNodeLikeControls(node, graphProps);
};

const isPieDatum = (
	menu: PieArcDatum<MenuOption> | MenuOption | RectMenuToolbarEntry
): menu is PieArcDatum<MenuOption> => {
	return (menu as PieArcDatum<MenuOption>).data !== undefined;
};

type RectMenuToolbarEntry = {
	layout: CardMenuLayoutEntry;
	option: MenuOption;
};

const getMenuOptionFromToolbarDatum = (
	datum: PieArcDatum<MenuOption> | MenuOption | RectMenuToolbarEntry
): MenuOption => {
	if (isPieDatum(datum)) {
		return datum.data;
	}
	if (datum && typeof datum === 'object' && 'option' in datum) {
		return datum.option;
	}
	return datum as MenuOption;
};

const disableOrEnableTools = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	aiStatus: boolean
): void => {
	nodes
		.selectAll<
			SVGGElement,
			PieArcDatum<MenuOption> | MenuOption | RectMenuToolbarEntry
		>('.nodes > g.menu-open .menu g:not(.move-connected)')
		.attr('class', (d) => {
			const menuData = getMenuOptionFromToolbarDatum(d);
			if (!menuData.enabled || (menuData.requiresAi && !aiStatus)) {
				return 'arc disabled';
			}
			return 'arc';
		});
};

const closeMenusForInactiveNodes = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>
): void => {
	nodes.selectAll('.nodes > g:not(.menu-open) .menu').call((menuEl) => {
		menuEl.selectAll('.arc, .move-connected').remove();
		return menuEl;
	});
};

export const updateNodeAndMenu = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	updateLabelInput(nodes, graphProps.nodeRadius);
	updateNodes(nodes, graphProps);
	updateNodeCircles(nodes, graphProps);
	updateInputSizesToFit(nodes, graphProps.nodeRadius);
	appendToolbarButtons(nodes, graphProps);
	disableOrEnableTools(nodes, graphProps.aiStatus);
	closeMenusForInactiveNodes(nodes);
	if (graphProps.graphMode !== 'ideate') {
		appendMoveConnected(nodes);
		appendDeleteButton(nodes, graphProps);
	}
};

/**
 * Function to use with .join to create a new node
 */
// export const enterNode = (
// 	elem: d3.Selection<d3.EnterElement, NCNode, SVGGElement, null>,
// 	graphProps: GraphProps,
// 	updateLabel: (event: InputEvent, finishedEditing?: boolean) => void,
// 	dragBehavior: d3.DragBehavior<SVGGElement, NCNode, unknown>
// ): d3.Selection<SVGGElement, NCNode, SVGGElement, null> => {
// 	const node = elem
// 		.append('g')
// 		.attr('id', (d) => `node-${d.uid}`)
// 		.classed('menu-open', false);

// 	node
// 		.on('mouseover', function () {
// 			this.classList.add('hover');
// 		})
// 		.on('mouseout', function () {
// 			this.classList.remove('hover');
// 		});

// 	appendCircleOrRect(node, graphProps);

// 	// Text input for node label
// 	const input = appendLabelInput(
// 		node.filter((d) => !d.src),
// 		graphProps.nodeRadius,
// 		graphProps.graphMode
// 	);
// 	if (graphProps.isEditable) {
// 		input.attr('readonly', (d) => (d.colour !== graphProps.userColourClass ? true : null));
// 		input.on('input', (event: InputEvent) => updateLabel(event));
// 		input.on('change', (event: InputEvent) => {
// 			updateLabel(event, true);
// 		});
// 		setTimeout(() => {
// 			const inputNode = input.node();
// 			if (inputNode && !inputNode.value) {
// 				inputNode.focus();
// 			}
// 		});
// 	} else {
// 		input.attr('readonly', true);
// 	}

// 	appendNodeImg(node.filter((d) => !!d.src && !d.isVideo));
// 	appendNodeVideo(node.filter((d) => Boolean(d.src) && Boolean(d.isVideo)));

// 	// Menu placeholder
// 	node.append('g').attr('class', 'menu');

// 	if (dragBehavior) {
// 		// Allow nodes to be dragged
// 		node.call(dragBehavior);
// 	}

// 	appendHearts(node);

// 	updateNodes(node, graphProps);

// 	return node;
// };

const isNodeEditable = (d: NCNode, graphProps: GraphProps) => {
	return d.colour === graphProps.userColourClass;
};

export const enterNode = (
	elem: d3.Selection<d3.EnterElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps,
	updateLabel: (event: InputEvent, finishedEditing?: boolean) => void,
	dragBehavior: d3.DragBehavior<SVGGElement, NCNode, unknown>,
	activateNode: (node: NCNode) => void,
	openNodeContent: (node: NCNode) => void
): d3.Selection<SVGGElement, NCNode, SVGGElement, null> => {
	const node = elem
		.append('g')
		.attr('id', (d) => `node-${d.uid}`)
		.classed('menu-open', false);

	node
		.on('mouseover', function () {
			this.classList.add('hover');
		})
		.on('mouseout', function () {
			this.classList.remove('hover');
		});

	appendCircleOrRect(node, graphProps);

	// Text input for node label
	const input = appendLabelInput(
		node.filter((d) => !d.src),
		graphProps.nodeRadius,
		graphProps.graphMode
	);
	console.log('[on graphProps.userColourClass]', graphProps.userColourClass);

	if (graphProps.isEditable) {
		input.attr('readonly', (d) => (d.colour !== graphProps.userColourClass ? true : null));
		input.on('keydown', (event: KeyboardEvent) => {
			if (event.key !== 'Enter' || event.isComposing) return;
			event.preventDefault();
			updateLabel(event as unknown as InputEvent, true);
		});
		input.on('input', (event: InputEvent) => {
			const isLineBreak = event.inputType === 'insertLineBreak';
			updateLabel(event, isLineBreak);
		});

		setTimeout(() => {
			const inputNode = input.node();

			if (inputNode && !inputNode.value) {
				// inputNode.focus();
				input.each(function (d) {
					// Focus the input if its colour equals the user colour class.
					if (d.colour === graphProps.userColourClass) {
						this.focus();
					}
				});
			}
		});
	} else {
		input.attr('readonly', true);
	}

	appendPdfDocumentCard(
		node.filter((d) => !d.src),
		graphProps.nodeRadius,
		activateNode,
		openNodeContent
	);
	appendPositioningOutputCard(node, graphProps.nodeRadius, activateNode, openNodeContent);
	appendGroundingEvidenceCard(node, graphProps.nodeRadius, activateNode, openNodeContent);
	appendSynthesiseOutputHeader(node, graphProps.nodeRadius, openNodeContent);
	appendExpandableTextControl(node, graphProps.nodeRadius, openNodeContent);

	appendNodeImg(
		node.filter((d) => !!d.src && !d.isVideo),
		activateNode,
		openNodeContent
	);
	appendNodeVideo(
		node.filter((d) => Boolean(d.src) && Boolean(d.isVideo)),
		activateNode,
		openNodeContent
	);

	// Menu placeholder
	node.append('g').attr('class', 'menu');

	if (dragBehavior) {
		// Allow nodes to be dragged
		node.call(dragBehavior);
	}

	appendNodeLikeControls(node, graphProps);
	appendBadgeAndLink(node);

	updateNodes(node, graphProps);
	return node;
};
const updateNodeCircles = (
	nodes: d3.Selection<SVGGElement, NCNode, SVGGElement, null>,
	graphProps: GraphProps
): void => {
	// Update radius to grow (or shrink) + fill colour
	nodes.select('circle').call((circleEl) =>
		circleEl
			.transition()
			.attr('r', (d) => {
				return graphProps.currentUser.activeNode === d.uid
					? graphProps.nodeRadius * 1.22
					: graphProps.nodeRadius;
			})
			.attr('fill', (d) => {
				// Find user for whom this node is active (if more than one, just use the first)
				const activeUser = graphProps.boardUsers.find((u) => u.activeNode === d.uid);
				if (activeUser) {
					return hexOrNamedColour(activeUser.colour);
				}
				if (d.nodeColour) {
					return hexOrNamedColour(d.nodeColour);
				}
				return '#fff';
			})
	);

	nodes.select('rect.node').call((rectEl) =>
		rectEl.transition().attr('fill', (d) => {
			// Find user for whom this node is active (if more than one, just use the first)
			const activeUser = graphProps.boardUsers.find((u) => u.activeNode === d.uid);
			if (activeUser) {
				return hexOrNamedColour(activeUser.colour);
			}
			if (d.nodeColour) {
				return hexOrNamedColour(d.nodeColour);
			}
			return '#fff';
		})
	);

	nodes
		.select(
			'.nodes > g:not(.menu-open) > foreignObject:not(.node-image):not(.node-video):not(.node-meta):not(.node-pdf-card-shell):not(.node-synthesise-header-shell)'
		)
		.classed('inversed', (d) => shouldUseInversedNodeText(d, graphProps.graphMode));

	// Set active node only to have menu-open class
	nodes.classed('menu-open', (d) => {
		return graphProps.currentUser.activeNode === d.uid;
	});
	nodes.classed('hidden', (d) => d.hidden || false);

	nodes.classed('dotted', (d) => d.dotted || false);

	// Sort to ensure menu overlays other nodes
	nodes.sort((a, b) => {
		const activeA = graphProps.currentUser.activeNode === a.uid;
		const activeB = graphProps.currentUser.activeNode === b.uid;
		if (activeA) {
			return 1;
		} else if (activeB) {
			return -1;
		}
		return 0;
	});
};
